import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { isEmpty, isNil } from 'lodash';
import { FormatedReportType } from '@views/pages/Reporting/Reports/types.ts';
import { cleanAnd } from '@common/cache';

export const reportingCsvDownloadRequest = async () => {
  const response = await axiosInstance.get(`/patient/pharmacy-report`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
  });

  const data = (await response).data;

  return data;
};

const createPatientReportRequest = async (
  props: paths['/reporting/patients/reports']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/reporting/patients/reports']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/reporting/patients/reports']['post']['responses']['201']['content']['application/json']
    >
  >(`/reporting/patients/reports`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreatePatientReportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['report', 'patient'],
    mutationFn: (
      props: paths['/reporting/patients/reports']['post']['requestBody']['content']['application/json'],
    ) => createPatientReportRequest(props),
  });

  return mutation;
};

const createUserReportRequest = async (
  props: paths['/reporting/users/reports']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/reporting/users/reports']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/reporting/users/reports']['post']['responses']['201']['content']['application/json']
    >
  >(`/reporting/users/reports`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateUserReportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['report', 'user'],
    mutationFn: (
      props: paths['/reporting/users/reports']['post']['requestBody']['content']['application/json'],
    ) => createUserReportRequest(props),
  });

  return mutation;
};

const createTaskReportRequest = async (
  props: paths['/reporting/tasks/reports']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/reporting/tasks/reports']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/reporting/tasks/reports']['post']['responses']['201']['content']['application/json']
    >
  >(`/reporting/tasks/reports`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateTaskReportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['report', 'task'],
    mutationFn: (
      props: paths['/reporting/tasks/reports']['post']['requestBody']['content']['application/json'],
    ) => createTaskReportRequest(props),
  });

  return mutation;
};

const createRecordReportRequest = async (
  props: paths['/reporting/record/reports']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/reporting/record/reports']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/reporting/record/reports']['post']['responses']['201']['content']['application/json']
    >
  >(`/reporting/record/reports`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateRecordReportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['report', 'record'],
    mutationFn: (
      props: paths['/reporting/record/reports']['post']['requestBody']['content']['application/json'],
    ) => createRecordReportRequest(props),
  });

  return mutation;
};

/////////////////////// DELETE REPORT ////////////////////

const deleteReportRequest = async (report_id: string) => {
  const response = axiosInstance.delete(`/reporting/reports/${report_id}`);
  const data = await response;

  return data;
};

export const useDeleteReportMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reportsDelete'],
    mutationFn: (report_id: string) => deleteReportRequest(report_id),
    onSuccess: () =>
      Promise.all([client.invalidateQueries({ queryKey: ['reports'] })]),
  });

  return mutation;
};

/////////////////////// MAKE REPORT FAVORITE ////////////////////

const makeReportFavoriteRequest = async (report_id: string) => {
  const response = axiosInstance.patch(
    `/reporting/reports/${report_id}/favorite`,
  );
  const data = await response;

  return data;
};

export const useMakeReportFavoriteMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reportsFavorite'],
    mutationFn: (report_id: string) => makeReportFavoriteRequest(report_id),
    onSuccess: (_, report_id) =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['reports'] }),
        client.setQueryData(
          ['report', report_id],
          (old: Record<string, unknown>) =>
            old ? { ...old, favorite: true } : old,
        ),
      ]),
  });

  return mutation;
};

/////////////////////// DELETE REPORT FAVORITE ////////////////////

const deleteReportFavoriteRequest = async (report_id: string) => {
  const response = axiosInstance.delete(
    `/reporting/reports/${report_id}/favorite`,
  );
  const data = await response;

  return data;
};

export const useDeleteReportFavoriteMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteReportsFavorite'],
    mutationFn: (report_id: string) => deleteReportFavoriteRequest(report_id),
    onSuccess: (_, report_id) =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['reports'] }),
        client.setQueryData(
          ['report', report_id],
          (old: Record<string, unknown>) =>
            old ? { ...old, favorite: false } : old,
        ),
      ]),
  });

  return mutation;
};

/////////////////////// DELETE REPORT FOLDER ////////////////////

const deleteReportFolderRequest = async (folder_id: string) => {
  const response = axiosInstance.delete(`/reporting/folders/${folder_id}`);
  const data = await response;

  return data;
};

export const useDeleteReportFolderMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reportFolderDelete'],
    mutationFn: (folder_id: string) => deleteReportFolderRequest(folder_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['reportFolders'] }),
        client.invalidateQueries({ queryKey: ['foldersNameListShort'] }),
        client.invalidateQueries({ queryKey: ['foldersShort'] }),
      ]),
  });

  return mutation;
};

/////////////////////// MAKE REPORT FOLDER FAVORITE ////////////////////

const makeReportFolderFavoriteRequest = async (folder_id: string) => {
  const response = axiosInstance.patch(
    `/reporting/folders/${folder_id}/favorite`,
  );
  const data = await response;

  return data;
};

export const useMakeReportFolderFavoriteMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reportFolderFavorite'],
    mutationFn: (folder_id: string) =>
      makeReportFolderFavoriteRequest(folder_id),
    onSuccess: () =>
      Promise.all([client.invalidateQueries({ queryKey: ['reportFolders'] })]),
  });

  return mutation;
};

/////////////////////// DELETE REPORT FOLDER FAVORITE ////////////////////

const deleteReportFolderFavoriteRequest = async (folder_id: string) => {
  const response = axiosInstance.delete(
    `/reporting/folders/${folder_id}/favorite`,
  );
  const data = await response;

  return data;
};

export const useDeleteReportFolderFavoriteMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteReportFolderFavorite'],
    mutationFn: (folder_id: string) =>
      deleteReportFolderFavoriteRequest(folder_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['reportFolders'] }),
        client.invalidateQueries({ queryKey: ['foldersNameListShort'] }),
      ]),
  });

  return mutation;
};

/////////////////////// ADD REPORT FOLDER REQUEST ////////////////////

const addReportFolderRequest = async (
  props: paths['/reporting/folders']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post(`/reporting/folders`, props);
  const data = await response;

  return data;
};

export const useAddReportFolderMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['addReportFolder'],
    mutationFn: (
      props: paths['/reporting/folders']['post']['requestBody']['content']['application/json'],
    ) => addReportFolderRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['reportFolders'] }),
        client.invalidateQueries({ queryKey: ['foldersNameListShort'] }),
      ]),
  });

  return mutation;
};

/////////////////////// EDIT REPORT FOLDER REQUEST ////////////////////

const editReportFolderRequest = async (
  props: paths['/reporting/folders/{report_folder_id}']['patch']['requestBody']['content']['application/json'],
  report_folder_id: string,
) => {
  const response = axiosInstance.patch(
    `/reporting/folders/${report_folder_id}`,
    props,
  );
  const data = await response;

  return data;
};

export const useEditReportFolderMutation = (report_folder_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editReportFolder'],
    mutationFn: (
      props: paths['/reporting/folders/{report_folder_id}']['patch']['requestBody']['content']['application/json'],
    ) => editReportFolderRequest(props, report_folder_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['reportFolder', report_folder_id],
        }),
        client.invalidateQueries({ queryKey: ['reportFolders'] }),
        client.invalidateQueries({ queryKey: ['foldersNameListShort'] }),
      ]),
  });

  return mutation;
};

const editPatientReportRequest = async (
  props: paths['/reporting/patients/reports/{report_id}']['patch']['requestBody']['content']['application/json'] & {
    report_id: string;
  },
) => {
  const formattedAnd = cleanAnd({ options: { $and: props.options?.$and } });
  const options = !isEmpty(props.options)
    ? { ...props.options, $and: formattedAnd.$and }
    : undefined;
  const response = axiosInstance.patch<
    paths['/reporting/patients/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    paths['/reporting/patients/reports/{report_id}']['patch']['responses']['204']['content']
  >(`/reporting/patients/reports/${props.report_id}`, {
    ...props,
    ...(!isEmpty(options) && { options }),
  });
  const data = await response;

  return data;
};

export const useEditPatientReportMutation = (report_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['patient', 'report', 'edit'],
    mutationFn: (
      props: paths['/reporting/patients/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    ) => editPatientReportRequest({ ...props, report_id }),
    onSuccess: () => [
      client.invalidateQueries({ queryKey: ['report', report_id] }),
      client.invalidateQueries({ queryKey: ['reports'] }),
    ],
  });

  return mutation;
};

const editUserReportRequest = async (
  props: paths['/reporting/users/reports/{report_id}']['patch']['requestBody']['content']['application/json'] & {
    report_id: string;
  },
) => {
  const formattedAnd = cleanAnd({ options: { $and: props.options?.$and } });
  const options = !isEmpty(props.options)
    ? { ...props.options, $and: formattedAnd.$and }
    : undefined;
  const response = axiosInstance.patch<
    paths['/reporting/users/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    paths['/reporting/users/reports/{report_id}']['patch']['responses']['204']['content']
  >(`/reporting/users/reports/${props.report_id}`, {
    ...props,
    ...(!isEmpty(options) && { options }),
  });
  const data = await response;

  return data;
};

export const useEditUserReportMutation = (report_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['user', 'report', 'edit'],
    mutationFn: (
      props: paths['/reporting/users/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    ) => editUserReportRequest({ ...props, report_id }),
    onSuccess: () => [
      client.invalidateQueries({ queryKey: ['report', report_id] }),
    ],
  });

  return mutation;
};

const editTaskReportRequest = async (
  props: paths['/reporting/tasks/reports/{report_id}']['patch']['requestBody']['content']['application/json'] & {
    report_id: string;
  },
) => {
  const formattedAnd = cleanAnd({ options: { $and: props.options?.$and } });
  const options = !isEmpty(props.options)
    ? { ...props.options, $and: formattedAnd.$and }
    : undefined;
  const response = axiosInstance.patch<
    paths['/reporting/tasks/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    paths['/reporting/tasks/reports/{report_id}']['patch']['responses']['200']['content']
  >(`/reporting/tasks/reports/${props.report_id}`, {
    ...props,
    ...(!isEmpty(options) && { options }),
  });
  const data = await response;

  return data;
};

export const useEditTaskReportMutation = (report_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['task', 'report', 'edit'],
    mutationFn: (
      props: paths['/reporting/tasks/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    ) => editTaskReportRequest({ ...props, report_id }),
    onSuccess: () => [
      client.invalidateQueries({ queryKey: ['report', report_id] }),
    ],
  });

  return mutation;
};

const editRecordReportRequest = async (
  props: paths['/reporting/record/reports/{report_id}']['patch']['requestBody']['content']['application/json'] & {
    report_id: string;
  },
) => {
  const formattedAnd = cleanAnd({ options: { $and: props.options?.$and } });
  const options = !isEmpty(props.options)
    ? { ...props.options, $and: formattedAnd.$and }
    : undefined;
  const response = axiosInstance.patch<
    paths['/reporting/record/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    paths['/reporting/record/reports/{report_id}']['patch']['responses']['200']['content']
  >(`/reporting/record/reports/${props.report_id}`, {
    ...props,
    ...(!isEmpty(options) && { options }),
  });
  const data = await response;

  return data;
};

export const useEditRecordReportMutation = (report_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['record', 'report', 'edit'],
    mutationFn: (
      props: paths['/reporting/record/reports/{report_id}']['patch']['requestBody']['content']['application/json'],
    ) => editRecordReportRequest({ ...props, report_id }),
    onSuccess: () => [
      client.invalidateQueries({ queryKey: ['report', report_id] }),
    ],
  });

  return mutation;
};

// +++++++++++++++++ Download REPORTS CSV +++++++++++++++

// The record entity is used for declaring the type here because the assumption is
// that all entities will share the same shape.
export type IReportsCsvDownloadProps = {
  report_id: string;
  report_type: FormatedReportType;
} & paths['/reporting/record/reports/{report_id}/csv']['get']['parameters']['query'];

export const reportCsvDownloadRequest = async ({
  report_type,
  report_id,
  ...props
}: IReportsCsvDownloadProps) => {
  const queryParams = new URLSearchParams(
    Object.entries(props as Record<string, string | boolean>)
      .filter(([_key, value]) => !isNil(value))
      .map(([key, value]) => [key, value.toString()]),
  );
  const response = await fetch(
    `${import.meta.env.VITE_BASE_URL}/reporting/${report_type}/reports/${report_id}/csv?${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    },
  );

  return response.body;
};

export const useReportCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['reportCSVDownload'],
    mutationFn: (props: IReportsCsvDownloadProps) =>
      reportCsvDownloadRequest(props),
  });

  return mutation;
};
